/**
 * The object registry: every placeable thing in the palette.
 *
 * This file is pure DATA — sprite keys, sizes, collision, and the id the level
 * file stores. Behaviour lives in `behaviours.ts`, keyed by these ids, so that
 * adding a gameplay rule never means touching the palette.
 *
 * Adding an object is three steps: an entry here, a sprite in
 * `src/engine/art/`, and optionally a behaviour in `behaviours.ts`.
 */

import { Solidity, type PaletteCategory } from './types'

export interface ObjectDef {
  /** Stable id stored in level JSON. Never rename without a migration. */
  id: string
  /** Human label shown in the editor palette. */
  name: string
  category: PaletteCategory
  /** Footprint in tiles. */
  w: number
  h: number
  /** Per-instance stretch axis, stored in placement props. */
  resize?: 'height' | 'width'
  /** Collision behaviour. */
  solidity: (typeof Solidity)[keyof typeof Solidity]
  /** Sprite key from the atlas. */
  sprite: string
  /**
   * Optional pixel hitbox inside the tile footprint, for objects whose
   * collision should be smaller than their art (pipes, springs, coins).
   */
  hitbox?: { w: number; h: number; ox?: number; oy?: number }
  /** Marks enemies: the player can stomp these. */
  enemy?: boolean
  /** Marks pickups: collected on touch. */
  pickup?: boolean
  /**
   * Marks a slippery surface: the player standing on these tiles picks up the
   * low-grip profile (see `ICE_MOVE`). A flag rather than a terrain id because
   * ice is a *placed object* here, and the terrain layer cannot describe one.
   */
  slippery?: boolean
  /** Marks instant-death surfaces. */
  lethal?: boolean
  /**
   * This object travels, so it is excluded from the tile collision grid and
   * collided against as a free-floating AABB instead.
   *
   * A moving platform cannot live in a tile grid. Its sprite is 16px tall, so
   * as it rises it straddles a tile boundary and occupies two rows; the player
   * standing on its true top surface ends up embedded in the upper row and gets
   * shoved up a whole tile. The original games solve this the same way: moving
   * platforms are objects, not metatiles.
   */
  dynamic?: boolean
  /** One-line hint shown on hover in the editor. */
  hint?: string
  /**
   * Marks a block an item can be dropped into.
   *
   * The editor's loading gesture writes `props.contains` on a container, and
   * the block's behaviour is responsible for paying that payload out. The flag
   * is what makes the drop land *on* an occupied tile instead of refusing it,
   * so a crate with a fixed payout of its own is still a container: the drop
   * replaces its default, which is the whole point of the gesture.
   */
  container?: boolean
  /**
   * The `contains` token this item becomes inside a container block.
   *
   * The token is not always the item's id (`ember-cell` loads as `ember`), so
   * the mapping lives on the def rather than being guessed from the name.
   */
  payload?: string
  /** Optional default props merged into placements. */
  props?: Record<string, number | string | boolean>
}

/**
 * Which entity kind each enemy *placement* becomes when a level loads.
 *
 * This is the functional source of truth for "is this an enemy": it is what
 * `Level.spawnObjects` consults to bring a placed enemy to life. It lives here,
 * beside the defs it names, rather than in `level.ts` — it is registry
 * knowledge, and keeping it in the runtime is what allowed a second, hand-written
 * `enemy: true` flag to exist alongside it and drift.
 *
 * Still pure data, which is what the old location was buying.
 */
export const ENEMY_KIND: Readonly<Record<string, string>> = {
  rivet: 'rivet',
  scout: 'scout',
  wanderer: 'wanderer',
  shardling: 'shardling',
  wisp: 'wisp',
}

/** Reverse of `ENEMY_KIND`, used to reap enemies on respawn. */
export const ENEMY_KIND_BY_ENTITY: ReadonlySet<string> = new Set(
  Object.values(ENEMY_KIND),
)

/**
 * How many payouts one container block can hold.
 *
 * Ten, like the coin block it is modelled on: a block that can be loaded
 * forever is a block that can be farmed forever, and the count is drawn on the
 * block in the editor, so the limit has to be a number a designer can see at a
 * glance. The editor stops incrementing here and `blockUseCount` clamps to it,
 * so a hand-written level file cannot exceed it either.
 */
export const MAX_BLOCK_CONTENTS = 10

/**
 * Uses a container block holds, from its `count` prop.
 *
 * `null` when the value cannot mean anything (absent, not a number), so a caller
 * that reports unusable props can tell "unset" from "unusable" while a caller
 * that only displays the badge can fall back with `?? 1`. A value outside the
 * range is clamped rather than rejected: `count: 0` is a usable request for the
 * smallest block, and no block holds ten thousand payouts.
 */
export function blockUseCount(raw: unknown): number | null {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null
  return Math.max(1, Math.min(MAX_BLOCK_CONTENTS, Math.round(raw)))
}

/**
 * Register a def, deriving the flags that must agree with the tables above.
 *
 * `enemy` used to be written by hand on each enemy def while `ENEMY_KIND` was
 * maintained separately. They agreed, and nothing checked, so adding an enemy
 * and forgetting one of the two produced a placement that looked like an enemy
 * in the editor, tinted as one in the debug overlay, and did nothing at all when
 * the level loaded. That is the same trap as a prop that is stored and never
 * read: accepted, displayed, discarded.
 *
 * Deriving it means the flag cannot disagree with the table that does the work.
 */
function def(d: ObjectDef): ObjectDef {
  const kind = ENEMY_KIND[d.id]
  if (kind !== undefined) return { ...d, enemy: true }
  // A def that *claims* to be an enemy while the table has no entry for it would
  // be the silent-inert-placement bug in a new place, so say so loudly at load.
  if (d.enemy === true) {
    console.warn(
      `objects: "${d.id}" is flagged enemy but has no entry in ENEMY_KIND, so a level placing it will spawn nothing`,
    )
    return { ...d, enemy: false }
  }
  return d
}

/**
 * Every placeable object. Ordered by category so the editor palette can render
 * it without sorting, and so the order is easy to reason about.
 */
export const OBJECT_DEFS: ObjectDef[] = [
  // ── Terrain-adjacent blocks ────────────────────────────────────────────
  def({
    id: 'hard-block',
    name: 'Alloy Block',
    category: 'blocks',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'hard-block',
    hint: 'Indestructible block.',
  }),
  def({
    id: 'brick',
    name: 'Cracked Tile',
    category: 'blocks',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'brick',
    hint: 'Bump from below to break. An expanded Tavi breaks it at once.',
  }),
  def({
    id: 'crate',
    name: 'Supply Crate',
    category: 'blocks',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'crate-block',
    container: true,
    hint: 'Bump to release what it holds. Drop an item on it to load it.',
    props: { contains: 'coin', count: 1 },
  }),
  def({
    id: 'crate-expansion-cell',
    name: 'Crate: Expansion Cell',
    category: 'blocks',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'crate-block',
    container: true,
    hint: 'Bump to release an expansion cell. Drop an item on it to load it.',
    props: { contains: 'expansion-cell', count: 1 },
  }),
  def({
    id: 'crate-ember-cell',
    name: 'Crate: Ember Cell',
    category: 'blocks',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'crate-block',
    container: true,
    hint: 'Bump to release an ember cell. Drop an item on it to load it.',
    props: { contains: 'ember', count: 1 },
  }),
  def({
    id: 'crate-prism',
    name: 'Crate: Prism',
    category: 'blocks',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'crate-block',
    container: true,
    hint: 'Bump to release a shield prism. Drop an item on it to load it.',
    props: { contains: 'prism', count: 1 },
  }),
  def({
    id: 'hidden-cache',
    name: 'Hidden Cache',
    category: 'blocks',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'hidden-cache',
    container: true,
    // `contains` is declared here for the same reason it is on `crate`: this
    // def's behaviour reads it (three course placements use it to hide a Spare Battery or
    // an Expansion Cell), and a prop a registry entry does not mention is a prop
    // nobody can find. The default is a spark shard, which is what a hidden block with
    // no opinion has always paid.
    hint: 'Invisible until bumped from below. Drop an item on it to load it.',
    props: { contains: 'coin', count: 1 },
  }),
  def({
    id: 'note-block',
    name: 'Pulse Pad',
    category: 'blocks',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'note-block',
    hint: 'Bump to launch Tavi high.',
  }),
  def({
    id: 'onoff',
    name: 'Relay Block',
    category: 'blocks',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'onoff-block',
    // "also acts as a lift" used to sit here. It was never true: `onoff` and
    // `onoff-block` are the same rule with the same body (`syncOnOff`), which
    // toggles the tile between solid and air and never writes `ox`/`oy`. A hint
    // is the only place a designer is told what a thing does, so a claim in one
    // that the code does not implement is the same defect as a prop that is read
    // by nothing — a promise kept by nothing.
    hint: 'Toggled by a switch, like the On/Off Platform.',
  }),
  def({
    id: 'onoff-block',
    name: 'Relay Platform',
    category: 'blocks',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'onoff-block',
    hint: 'Only solid while a switch is active.',
  }),
  def({
    id: 'ice-block',
    name: 'Glass Tile',
    category: 'blocks',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'ice-block',
    slippery: true,
    hint: 'Slippery surface.',
  }),
  def({
    id: 'sand',
    name: 'Copper Fill',
    category: 'blocks',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'sand',
    hint: 'Solid fill.',
  }),

  // ── Pipes ──────────────────────────────────────────────────────────────
  def({
    id: 'pipe',
    name: 'Vent Column',
    category: 'pipes',
    w: 2,
    h: 2,
    solidity: Solidity.FULL,
    sprite: 'pipe-top-left',
    resize: 'height',
    hint: 'Place one complete vent. Drag its yellow top handle to change height.',
  }),
  // ── Platforms ──────────────────────────────────────────────────────────
  def({
    id: 'platform',
    resize: 'width',
    name: 'Catwalk',
    category: 'platforms',
    w: 1,
    h: 1,
    solidity: Solidity.ONEWAY,
    sprite: 'platform',
    hint: 'Land on it from above; pass through from below. Down jumps through.',
  }),
  def({
    id: 'bridge',
    resize: 'width',
    name: 'Cable Bridge',
    category: 'platforms',
    w: 1,
    h: 1,
    solidity: Solidity.ONEWAY,
    sprite: 'bridge',
    hint: 'Cable bridge. Solid only from above.',
  }),
  def({
    id: 'moving-platform',
    resize: 'width',
    name: 'Lift',
    category: 'platforms',
    w: 2,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'moving-platform',
    dynamic: true,
    // The hint used to say "Right-click to set the distance". The editor has no
    // prop editor at all — the right button is the eyedropper — so the one piece
    // of authoring guidance a designer gets for these two props pointed at a
    // gesture that picks a different object. `range` and `speed` are authored in
    // the course's `place()` call or in the saved JSON; the hint says so.
    hint: 'Travels up and down. `range` in tiles, `speed` in px/frame.',
    props: { range: 3, speed: 0.5 },
  }),
  def({
    id: 'moving-platform-h',
    resize: 'width',
    name: 'Shuttle',
    category: 'platforms',
    w: 2,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'moving-platform',
    dynamic: true,
    hint: 'Travels left and right. `range` in tiles, `speed` in px/frame.',
    props: { range: 4, speed: 0.5 },
  }),

  // ── Hazards ────────────────────────────────────────────────────────────
  def({
    id: 'spike',
    name: 'Crystal Spikes',
    category: 'hazards',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'spike',
    lethal: true,
    hitbox: { w: 16, h: 10, oy: 6 },
    hint: 'Instantly lethal.',
  }),
  def({
    id: 'lava',
    name: 'Flux Pool',
    category: 'hazards',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'lava-top',
    lethal: true,
    hitbox: { w: 16, h: 8, oy: 8 },
    hint: 'Instantly lethal.',
  }),
  def({
    id: 'saw',
    name: 'Rotor',
    category: 'hazards',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'saw',
    lethal: true,
    hitbox: { w: 14, h: 14, ox: 1, oy: 1 },
    // Was "Right-click to set the range" — see the note on `moving-platform`.
    // `spin` is declared here rather than only in `rideTrack` because that
    // function has read it since the beginning, and a prop nothing declares is
    // invisible to the registry: it cannot be discovered, defaulted, or checked.
    hint: 'Moves along a track. `range` in tiles, `speed` in px/frame.',
    props: { range: 4, speed: 0.4, spin: 0.3 },
  }),
  def({
    id: 'saw-h',
    name: 'Rotor (horizontal)',
    category: 'hazards',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'saw',
    lethal: true,
    hitbox: { w: 14, h: 14, ox: 1, oy: 1 },
    hint: 'Moves side to side. `range` in tiles, `speed` in px/frame.',
    props: { range: 5, speed: 0.4, spin: 0.3 },
  }),

  // ── Enemies ────────────────────────────────────────────────────────────
  def({
    id: 'rivet',
    name: 'Rivet Crawler',
    category: 'enemies',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'rivet-walk1',
    hitbox: { w: 14, h: 14, ox: 1, oy: 2 },
    hint: 'Walks and turns at ledges and walls. Stomp it.',
  }),
  def({
    id: 'scout',
    name: 'Wheel Scout',
    category: 'enemies',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'scout-walk1',
    hitbox: { w: 12, h: 14, ox: 2, oy: 2 },
    hint: 'Stomp to turn it into a wheel. Kick the wheel to slide it.',
  }),
  def({
    id: 'wanderer',
    name: 'Wandering Scout',
    category: 'enemies',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'scout-walk1',
    hitbox: { w: 12, h: 14, ox: 2, oy: 2 },
    hint: 'Wanders without avoiding ledges.',
  }),
  def({
    id: 'shardling',
    name: 'Shard Crawler',
    category: 'enemies',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'shardling-walk1',
    hitbox: { w: 14, h: 14, ox: 1, oy: 2 },
    hint: 'Cannot be stomped — its spikes hurt on every contact.',
  }),
  def({
    id: 'wisp',
    name: 'Lens Wisp',
    category: 'enemies',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'wisp',
    hitbox: { w: 14, h: 14, ox: 1, oy: 1 },
    // `dir` is the heading the wisp rests on, and it is the one prop in this
    // registry that is read by an *entity* rather than by `behaviours.ts`, which
    // is exactly how it came to be dropped: a placed enemy used to be spawned
    // from its tile alone, so a wisp authored `{ dir: 1 }` kept the prop on the
    // object and faced the other way in play. Declaring the default here means
    // the prop is visible in the one file that documents what an object can be
    // told, and a placement that says nothing merges to the sprite's own facing.
    hint: 'Floats straight at Tavi; ignores terrain. Stomp it.',
    props: { dir: -1 },
  }),

  // ── Items ──────────────────────────────────────────────────────────────
  def({
    id: 'coin',
    name: 'Spark Shard',
    category: 'items',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'coin',
    pickup: true,
    payload: 'coin',
    hitbox: { w: 10, h: 14, ox: 3, oy: 1 },
    hint: 'Collect for one spark shard. Drop it on a crate to load the block.',
  }),
  def({
    id: 'expansion-cell',
    name: 'Expansion Cell',
    category: 'items',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'expansion-cell',
    pickup: true,
    payload: 'expansion-cell',
    hint: 'Grow to the expanded chassis. Drop it on a crate to load the block.',
  }),
  def({
    id: 'ember-cell',
    name: 'Ember Cell',
    category: 'items',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'ember-cell',
    pickup: true,
    payload: 'ember',
    hint: 'Adds a short-range fireball attack. Drop it on a crate to load the block.',
  }),
  def({
    id: 'prism',
    name: 'Shield Prism',
    category: 'items',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'prism',
    pickup: true,
    payload: 'prism',
    hint: 'Invincible for a few seconds. Flashing. Drop it on a crate to load the block.',
  }),
  def({
    id: 'spare-battery',
    name: 'Spare Battery',
    category: 'items',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'spare-battery',
    pickup: true,
    payload: 'spare-battery',
    hint: 'Extra life. Drop it on a crate to load the block.',
  }),

  // ── Interactive / scenery ──────────────────────────────────────────────
  def({
    id: 'spring',
    name: 'Coil Launcher',
    category: 'hazards',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'spring',
    hitbox: { w: 16, h: 8, oy: 8 },
    hint: 'Bounces Tavi very high.',
  }),
  def({
    id: 'vine',
    name: 'Climbing Cable',
    category: 'scenery',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'vine',
    hitbox: { w: 8, h: 16, ox: 4 },
    hint: 'Climbable.',
  }),
  def({
    id: 'checkpoint',
    name: 'Signal Beacon',
    category: 'scenery',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'checkpoint-flag',
    hitbox: { w: 16, h: 16 },
    hint: 'Sets the respawn point when touched.',
  }),
  def({
    id: 'goal',
    name: 'Exit Beacon',
    category: 'scenery',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'goal-flag',
    hitbox: { w: 16, h: 16 },
    hint: 'Reach this to clear the course.',
  }),
  def({
    id: 'castle',
    name: 'Observatory',
    category: 'scenery',
    w: 4,
    h: 4,
    solidity: Solidity.NONE,
    sprite: 'goal-castle',
    hint: 'Decorative. A course is finished at the exit beacon, not here.',
  }),
  def({
    id: 'cannon',
    name: 'Ember Turret',
    category: 'enemies',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'cannon',
    hitbox: { w: 16, h: 10, oy: 6 },
    hint: 'Fires fireballs to the right.',
  }),
  def({
    id: 'switch',
    name: 'Relay Switch',
    category: 'scenery',
    w: 1,
    h: 1,
    solidity: Solidity.FULL,
    sprite: 'onoff-block',
    hitbox: { w: 16, h: 16 },
    hint: 'Toggles every On/Off platform in the level.',
  }),

  // ── Scenery ────────────────────────────────────────────────────────────
  def({
    id: 'cloud',
    name: 'High Wisps',
    category: 'scenery',
    w: 2,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'cloud',
  }),
  def({
    id: 'cloud2',
    name: 'Low Wisps',
    category: 'scenery',
    w: 2,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'cloud2',
  }),
  def({
    id: 'bush',
    name: 'Crystal Cluster',
    category: 'scenery',
    w: 2,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'bush',
  }),
  def({
    id: 'hill',
    name: 'Crystal Ridge',
    category: 'scenery',
    w: 3,
    h: 2,
    solidity: Solidity.NONE,
    sprite: 'hill-lg',
  }),
  def({
    id: 'fence',
    name: 'Cable Fence',
    category: 'scenery',
    w: 1,
    h: 1,
    solidity: Solidity.NONE,
    sprite: 'fence',
  }),
]

/** Fast lookup by id. */
export const OBJECT_BY_ID: ReadonlyMap<string, ObjectDef> = new Map(
  OBJECT_DEFS.map((d) => [d.id, d]),
)

/**
 * The palette item that writes each `contains` token, derived from the defs so
 * it cannot disagree with what a drop stores.
 *
 * The editor needs this direction to draw a loaded block's badge: the level file
 * stores the token (`ember`), and the picture of it lives on the item def
 * (`ember-cell`), whose id is deliberately not the token. A token nothing
 * declares is simply not in the map, which is how the badge skips a block whose
 * `contains` value the behaviour is already warning about.
 */
export const ITEM_FOR_PAYLOAD: ReadonlyMap<string, ObjectDef> = new Map(
  OBJECT_DEFS.flatMap((d) =>
    d.payload === undefined ? [] : [[d.payload, d] as const],
  ),
)

/** Palette order, grouped by category. */
export const CATEGORY_ORDER: PaletteCategory[] = [
  'terrain',
  'blocks',
  'pipes',
  'platforms',
  'hazards',
  'enemies',
  'items',
  'scenery',
]

/** Objects grouped by category, in registry order. */
export function objectsByCategory(): Map<PaletteCategory, ObjectDef[]> {
  const out = new Map<PaletteCategory, ObjectDef[]>()
  for (const c of CATEGORY_ORDER) out.set(c, [])
  for (const d of OBJECT_DEFS) out.get(d.category)?.push(d)
  return out
}

/** Resolve dimensions once so drawing, picking and collision share a footprint. */
export function placedObjectDef(
  id: string,
  props: Record<string, unknown> = {},
): ObjectDef | undefined {
  const base = OBJECT_BY_ID.get(id)
  if (!base || !base.resize) return base
  const axis = base.resize
  const raw = props[axis]
  const min = axis === 'height' ? base.h : base.w
  const size =
    typeof raw === 'number' && Number.isFinite(raw)
      ? Math.max(min, Math.min(32, Math.round(raw)))
      : min
  return {
    ...base,
    w: axis === 'width' ? size : base.w,
    h: axis === 'height' ? size : base.h,
  }
}
